import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Users,
  Plane,
  BedDouble,
  Bus,
  QrCode,
  Luggage,
  FileSpreadsheet,
  TrendingUp,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import useFetch from '../../hooks/useFetch';
import useChartMotion from '../../hooks/useChartMotion';
import { download } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Card,
  Stat,
  Button,
  Loader,
  EmptyState,
  SectionTitle,
  Select,
  Table,
  label,
} from '../../components/UI';
import { fmtNumber } from '../../utils/format';
const EXPORTS = [
  { type: 'participants', label: 'Participants', icon: Users, hint: 'Names, contacts, departments and document status' },
  { type: 'attendance', label: 'Attendance', icon: QrCode, hint: 'Every scan with time, gate and who scanned it' },
  { type: 'travel', label: 'Travel', icon: Plane, hint: 'Flights, PNRs, seats and ticket status' },
  { type: 'rooms', label: 'Rooms', icon: BedDouble, hint: 'Allocations, partners, check-in and check-out' },
  { type: 'buses', label: 'Transport', icon: Bus, hint: 'Coach manifests with boarding status' },
  { type: 'luggage', label: 'Luggage', icon: Luggage, hint: 'Tags, owners, delivery and confirmations' },
];

const PIE_COLORS = ['#EFD48A', '#60A5FA', '#4ADE80', '#FBBF24', '#F87171', '#C9A54D'];

export default function Reports() {
  const { eventId, events } = useEvents();
  const toast = useToast();
  const [busy, setBusy] = useState(null);
  const [groupBy, setGroupBy] = useState('department');

  const { data: report, loading } = useFetch(
    eventId ? `/reports/event/${eventId}` : null, {}, { skip: !eventId },
  );
  const { data: byGroup } = useFetch(
    '/reports/attendance', { event: eventId, groupBy }, { skip: !eventId },
  );
  const chartMotion = useChartMotion();

  if (!events.length) {
    return <EmptyState message="No events available" hint="Reports become available once you have an event." icon={BarChart3} />;
  }

  const runExport = async (type, text) => {
    setBusy(type);
    try {
      await download('/reports/export', { type, event: eventId }, `${text.toLowerCase()}-report.xlsx`);
      toast.success(`${text} report downloaded`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const sessionChart = (report?.attendance?.perSession || []).slice(0, 8);
  const luggagePie = report ? [
    { name: 'Delivered', value: report.luggage.delivered },
    { name: 'In progress', value: Math.max(report.luggage.total - report.luggage.delivered - report.luggage.missing, 0) },
    { name: 'Missing', value: report.luggage.missing },
  ].filter((d) => d.value > 0) : [];

  return (
    <>
      <SectionTitle
        title="Reports & analytics"
        subtitle="Event performance, compliance and downloadable spreadsheets"
        action={<EventPicker />}
      />

      {loading ? <Loader label="Building the report" /> : report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 13, marginBottom: 20 }}>
            <Stat label="Participants" value={fmtNumber(report.participants.total)} icon={Users} tone="info"
              sub={`${report.participants.vips} VIP guests`} />
            <Stat label="Check-in Rate" value={`${report.participants.checkInRate}%`} icon={QrCode} tone="success"
              progress={report.participants.checkInRate} sub={`${report.participants.checkedIn} arrived`} />
            <Stat label="Travel Compliance" value={`${report.travel.complianceRate}%`} icon={Plane} tone="gold"
              progress={report.travel.complianceRate} sub={`${report.travel.ticketed} tickets issued`} />
            <Stat label="Room Occupancy" value={`${report.stay.occupancyRate}%`} icon={BedDouble} tone="warning"
              progress={report.stay.occupancyRate} sub={`${report.stay.occupied} of ${report.stay.rooms} rooms`} />
            <Stat label="Avg. Attendance" value={`${report.attendance.averageAttendance}%`} icon={TrendingUp} tone="info"
              progress={report.attendance.averageAttendance} sub={`${report.event.sessions} sessions`} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 16, marginBottom: 20 }} className="gl-rep-grid">
            <Card>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Attendance by session</h3>
              <p style={{ fontSize: 12, color: 'var(--text-lo)', marginBottom: 16 }}>
                How many participants were scanned into each session
              </p>
              {sessionChart.length ? (
                <ResponsiveContainer width="100%" height={276}>
                  <BarChart data={sessionChart} margin={{ top: 6, right: 8, left: -20, bottom: 44 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#24242B" vertical={false} />
                    <XAxis dataKey="title" tick={{ fill: '#71717F', fontSize: 10 }} axisLine={false} tickLine={false}
                      angle={-28} textAnchor="end" height={62} interval={0}
                      tickFormatter={(v) => (v.length > 20 ? `${v.slice(0, 20)}…` : v)} />
                    <YAxis tick={{ fill: '#71717F', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: 'rgba(239,212,138,0.06)' }}
                      contentStyle={{ background: '#16161A', border: '1px solid #24242B', borderRadius: 12, fontSize: 12 }}
                      labelStyle={{ color: '#EFD48A', fontWeight: 600 }} />
                    <Bar dataKey="present" fill="#EFD48A" radius={[7, 7, 0, 0]} maxBarSize={58} name="Present" {...chartMotion} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyState message="No session attendance recorded yet" icon={QrCode} />}
            </Card>

            <Card>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Luggage status</h3>
              <p style={{ fontSize: 12, color: 'var(--text-lo)', marginBottom: 16 }}>
                {report.luggage.total} bag{report.luggage.total === 1 ? '' : 's'} registered
              </p>
              {luggagePie.length ? (
                <ResponsiveContainer width="100%" height={276}>
                  <PieChart>
                    <Pie data={luggagePie} dataKey="value" nameKey="name" cx="50%" cy="45%"
                      innerRadius={54} outerRadius={88} paddingAngle={3} {...chartMotion}>
                      {luggagePie.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i]} stroke="none" />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#16161A', border: '1px solid #24242B', borderRadius: 12, fontSize: 12 }} />
                    <Legend verticalAlign="bottom" iconType="circle" iconSize={8}
                      wrapperStyle={{ fontSize: 12, color: '#A8A8B3' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyState message="No luggage registered" icon={Luggage} />}
            </Card>
          </div>

          {/* group breakdown */}
          <Card style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 600 }}>Attendance breakdown</h3>
                <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 3 }}>
                  Average session attendance rolled up by group
                </p>
              </div>
              <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={{ width: 175 }}>
                <option value="department">By department</option>
                <option value="designation">By designation</option>
              </Select>
            </div>

            <Table
              rows={byGroup?.rows}
              keyField="group"
              empty="No attendance data to group yet"
              columns={[
                { key: 'group', title: label(groupBy), render: (r) => (
                  <span style={{ fontWeight: 500 }}>{r.group}</span>
                )},
                { key: 'people', title: 'People', align: 'right', render: (r) => r.people },
                { key: 'totalAttended', title: 'Sessions attended', align: 'right', render: (r) => r.totalAttended },
                { key: 'averagePercentage', title: 'Average attendance', render: (r) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 150 }}>
                    <div style={{ flex: 1, height: 6, background: 'var(--ink-700)', borderRadius: 99, overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: 0 }} animate={{ width: `${r.averagePercentage}%` }}
                        transition={{ duration: 0.8 }}
                        style={{
                          height: '100%', borderRadius: 99,
                          background: r.averagePercentage >= 70 ? 'var(--ok)'
                            : r.averagePercentage >= 40 ? 'var(--gold-400)' : 'var(--danger)',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', minWidth: 38 }}>
                      {r.averagePercentage}%
                    </span>
                  </div>
                )},
              ]}
            />
          </Card>
        </>
      )}

      {/* ── exports ──────────────────────────────────────────────── */}
      <SectionTitle title="Download reports" subtitle="Excel workbooks ready to hand to finance, HR or the client" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(255px, 1fr))', gap: 13 }}>
        {EXPORTS.map((x, i) => (
          <motion.div key={x.type} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card hover padding={17} style={{ height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 'var(--r-sm)', flexShrink: 0,
                  background: 'rgba(239,212,138,0.11)', display: 'grid', placeItems: 'center',
                }}>
                  <x.icon size={17} color="var(--gold-400)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h4 style={{ fontSize: 13.8, fontWeight: 600 }}>{x.label}</h4>
                  <p style={{ fontSize: 11.8, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.55 }}>{x.hint}</p>
                </div>
              </div>
              <Button
                variant="ghost" size="sm" icon={FileSpreadsheet}
                style={{ width: '100%', marginTop: 14 }}
                loading={busy === x.type}
                disabled={!eventId}
                onClick={() => runExport(x.type, x.label)}
              >
                Download .xlsx
              </Button>
            </Card>
          </motion.div>
        ))}
      </div>

      <style>{`@media (max-width: 1000px) { .gl-rep-grid { grid-template-columns: 1fr !important; } }`}</style>
    </>
  );
}
